<template>
  <div class="certificate-page">
    <div class="certificate-actions">
      <el-button type="primary" @click="downloadCertificate" size="medium" :loading="downloading">
        Download Certificate
      </el-button>
    </div>

    <div class="certificate-wrapper" ref="certificateRef">
      <!-- Membership Certificate -->
      <div v-if="certificateType === 'membership'" class="cert-container membership-cert">
        <img :src="membershipBg" class="cert-bg" crossorigin="anonymous" />
        <div class="cert-content">
          <div class="cert-description">This certificate is awarded to</div>
          <div class="cert-name">{{ userName }}</div>
          <div class="cert-description">in recognition of official membership in</div>
          <div class="cert-club-name">{{ clubName }}</div>
          <div class="cert-school-name">{{ schoolName }}</div>
          <div class="cert-description">Term of Membership:</div>
          <div class="cert-tenure">{{ tenurePeriod }}</div>
        </div>
        <div class="cert-footer">
          <div class="cert-footer-title">DIGITALLY CERTIFIED · ECLUB SYSTEM</div>
          <div class="cert-footer-row">
            <div class="cert-footer-item">
              <div class="cert-footer-label">CERTIFICATE ID</div>
              <div class="cert-footer-value">{{ certificateId }}</div>
            </div>
            <div class="cert-footer-qr">
              <vue-qr :text="certificateUrl" :size="400" :margin="0" :correctLevel="1" colorDark="#FF6450"></vue-qr>
            </div>
            <div class="cert-footer-item">
              <div class="cert-footer-label">ISSUE DATE</div>
              <div class="cert-footer-value">{{ issuedDate }}</div>
            </div>
          </div>
          <div class="cert-verify-url">eClub | https://www.eclub.center</div>
        </div>
      </div>

      <!-- Leadership Certificate -->
      <div v-if="certificateType === 'leadership'" class="cert-container leadership-cert">
        <img :src="leadershipBg" class="cert-bg" crossorigin="anonymous" />
        <div class="cert-content">
          <div class="cert-description">This certificate is awarded to</div>
          <div class="cert-name">{{ userName }}</div>
          <div class="cert-description">for serving as</div>
          <div class="cert-position">{{ positionName }}</div>
          <div class="cert-description">of</div>
          <div class="cert-club-name">{{ clubName }}</div>
          <div class="cert-school-name">{{ schoolName }}</div>
          <div class="cert-description">Term of Office:</div>
          <div class="cert-tenure">{{ tenurePeriod }}</div>
        </div>
        <div class="cert-footer">
          <div class="cert-footer-title">DIGITALLY CERTIFIED · ECLUB GLOBAL SYSTEM</div>
          <div class="cert-footer-row">
            <div class="cert-footer-item">
              <div class="cert-footer-label">CERTIFICATE ID</div>
              <div class="cert-footer-value">{{ certificateId }}</div>
            </div>
            <div class="cert-footer-qr">
              <vue-qr :text="certificateUrl" :size="400" :margin="0" :correctLevel="1" colorDark="#FF6450"></vue-qr>
            </div>
            <div class="cert-footer-item">
              <div class="cert-footer-label">ISSUE DATE</div>
              <div class="cert-footer-value">{{ issuedDate }}</div>
            </div>
          </div>
          <div class="cert-verify-url">eClub | https://www.eclub.center</div>
        </div>
      </div>

      <!-- Advisory Certificate -->
      <div v-if="certificateType === 'advisory'" class="cert-container advisory-cert">
        <img :src="advisoryBg" class="cert-bg" crossorigin="anonymous" />
        <div class="cert-content">
          <div class="cert-name">{{ userName }}</div>
          <div class="cert-email">{{ userEmail }}</div>
          <div class="cert-role-label">DISTINGUISHED SERVICE AS</div>
          <div class="cert-position">Faculty Advisor</div>
          <div class="cert-club-name">{{ clubName }}</div>
          <div class="cert-school-name">{{ schoolName }}</div>
          <div class="cert-tenure">Advisory Period: {{ tenurePeriod }}</div>
        </div>
        <div class="cert-footer">
          <div class="cert-footer-title">DIGITALLY CERTIFIED · ECLUB GLOBAL SYSTEM</div>
          <div class="cert-footer-row">
            <div class="cert-footer-item">
              <div class="cert-footer-label">CERTIFICATE ID</div>
              <div class="cert-footer-value">{{ certificateId }}</div>
            </div>
            <div class="cert-footer-qr">
              <vue-qr :text="certificateUrl" :size="400" :margin="0" :correctLevel="1" colorDark="#FF6450"></vue-qr>
            </div>
            <div class="cert-footer-item">
              <div class="cert-footer-label">ISSUE DATE</div>
              <div class="cert-footer-value">{{ issuedDate }}</div>
            </div>
          </div>
          <div class="cert-verify-url">eClub | https://www.eclub.center</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getUserName, getUserEmail, getSchoolName } from '@/utils/store'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import VueQr from 'vue-qr'
import membershipBg from '@/assets/image/certificates/certified_membership.png'
import leadershipBg from '@/assets/image/certificates/certified_leadership.png'
import advisoryBg from '@/assets/image/certificates/certified_advisory.png'

export default {
  name: 'CertificateDisplay',
  components: {
    VueQr
  },
  data() {
    return {
      membershipBg,
      leadershipBg,
      advisoryBg,
      certificateType: '',
      userName: '',
      userEmail: '',
      clubName: '',
      schoolName: '',
      positionName: '',
      tenureStart: '',
      tenureEnd: '',
      certificateId: '',
      issuedDate: '',
      downloading: false
    }
  },

  computed: {
    tenurePeriod() {
      if (!this.tenureStart && !this.tenureEnd) {
        return 'Current'
      }
      const start = this.formatDateForDisplay(this.tenureStart)
      const end = this.tenureEnd ? this.formatDateForDisplay(this.tenureEnd) : 'Present'
      return `${start} – ${end}`
    },
    certificateUrl() {
      // Build the public certificate URL so the QR code points to this exact
      // certificate page without requiring login.
      const query = this.$route.query || {}
      const params = new URLSearchParams()
      Object.keys(query).forEach(k => {
        if (query[k] !== undefined && query[k] !== null && query[k] !== '') {
          params.append(k, query[k])
        }
      })
      return `${window.location.origin}/#/certificate?${params.toString()}`
    }
  },

  created() {
    this.initializeFromQuery()
  },

  methods: {
    initializeFromQuery() {
      const query = this.$route.query
      this.certificateType = query.type || 'membership'
      this.clubName = query.club_name || ''
      this.schoolName = query.school_name || getSchoolName() || ''
      this.userName = query.user_name || getUserName() || ''
      this.userEmail = query.user_email || getUserEmail() || ''
      this.positionName = query.position || ''
      this.tenureStart = query.tenure_start || ''
      this.tenureEnd = query.tenure_end || ''

      this.certificateId = this.generateCertificateId()
      this.issuedDate = this.formatIssueDate(this.tenureEnd)
    },

    generateCertificateId() {
      // Deterministic, opaque identifier derived from the certificate's
      // identifying fields. Same inputs always produce the same id, but the
      // string is intentionally unreadable so it doesn't leak structure.
      const seed = [
        this.certificateType,
        (this.userEmail || '').toLowerCase(),
        (this.userName || '').toLowerCase(),
        (this.clubName || '').toLowerCase(),
        (this.schoolName || '').toLowerCase(),
        this.positionName || '',
        this.tenureStart || '',
        this.tenureEnd || ''
      ].join('|')

      // Two independent 32-bit hashes give us ~64 bits of entropy.
      let h1 = 0x811c9dc5
      for (let i = 0; i < seed.length; i++) {
        h1 ^= seed.charCodeAt(i)
        h1 = (h1 + ((h1 << 1) + (h1 << 4) + (h1 << 7) + (h1 << 8) + (h1 << 24))) >>> 0
      }
      let h2 = 5381
      for (let i = 0; i < seed.length; i++) {
        h2 = (((h2 << 5) + h2) ^ seed.charCodeAt(i)) >>> 0
      }

      const part1 = h1.toString(36).toUpperCase().padStart(7, '0')
      const part2 = h2.toString(36).toUpperCase().padStart(7, '0')
      const raw = (part1 + part2).slice(0, 14)
      return `${raw.slice(0, 4)}-${raw.slice(4, 9)}-${raw.slice(9, 14)}`
    },

    formatDateForDisplay(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const months = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December']
      return `${months[date.getMonth()]} ${date.getFullYear()}`
    },

    formatIssueDate(dateStr) {
      if (!dateStr) {
        const today = new Date()
        const y = today.getFullYear()
        const m = String(today.getMonth() + 1).padStart(2, '0')
        const d = String(today.getDate()).padStart(2, '0')
        return `${y}-${m}-${d}`
      }
      // Already in YYYY-MM-DD form when passed from list pages.
      return dateStr
    },

    async downloadCertificate() {
      this.downloading = true
      try {
        const element = this.$refs.certificateRef
        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff'
        })

        const imgData = canvas.toDataURL('image/png')
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        })

        const pdfWidth = pdf.internal.pageSize.getWidth()
        const pdfHeight = pdf.internal.pageSize.getHeight()

        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight)
        pdf.save(`${this.certificateType}_certificate_${this.certificateId}.pdf`)
      } catch (error) {
        console.error('Error generating PDF:', error)
        this.$message.error('Failed to generate PDF. Please try again.')
      } finally {
        this.downloading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.certificate-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.certificate-actions {
  margin-bottom: 20px;
  display: flex;
  gap: 12px;
}

.certificate-wrapper {
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.cert-container {
  position: relative;
  width: 595px;
  height: 842px;
  font-family: 'Segoe UI', 'Arial', sans-serif;
  overflow: hidden;

  .cert-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 1;
  }

  .cert-content {
    position: absolute;
    top: 29%;
    left: 0;
    width: 100%;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .cert-name {
    font-size: 36px;
    font-weight: 600;
    color: #FF6450;
    text-align: center;
    margin-bottom: 16px;
  }

  .cert-email {
    font-size: 16px;
    color: #999;
    text-align: center;
    margin-bottom: 24px;
  }

  .cert-role-label {
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 2px;
    color: #333;
    text-align: center;
    margin-bottom: 16px;
  }

  .cert-position {
    font-size: 28px;
    font-weight: 600;
    color: #FF6450;
    text-align: center;
    margin-bottom: 16px;
  }

  .cert-club-name {
    font-size: 28px;
    font-weight: 600;
    color: #FF6450;
    text-align: center;
    margin-bottom: 12px;
  }

  .cert-school-name {
    font-size: 18px;
    color: #999;
    text-align: center;
    margin-bottom: 24px;
  }

  .cert-tenure {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    text-align: center;
    margin-bottom: 20px;
  }

  .cert-description {
    font-size: 14px;
    line-height: 1.6;
    color: #666;
    text-align: center;
    margin-bottom: 12px;
  }

  .cert-footer {
    position: absolute;
    bottom: 6%;
    left: 0;
    width: 100%;
    text-align: center;
    z-index: 2;

    .cert-footer-title {
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 2px;
      color: #FF6450;
      margin-bottom: 12px;
    }

    .cert-footer-row {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 28px;
      margin-bottom: 10px;
    }

    .cert-footer-qr {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 6px;
      background: #fff;
      border-radius: 4px;

      ::v-deep img {
        display: block;
        width: 90px;
        height: 90px;
        image-rendering: pixelated;
        image-rendering: crisp-edges;
      }
    }

    .cert-footer-item {
      text-align: center;
      min-width: 110px;

      .cert-footer-label {
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 1px;
        color: #333;
        margin-bottom: 4px;
      }

      .cert-footer-value {
        font-size: 12px;
        color: #FF6450;
      }
    }

    .cert-verify-url {
      font-size: 12px;
      color: #FF6450;
      position: relative;
      top: 14px;
    }
  }
}

// Membership certificate specific styles
.membership-cert {
  .cert-content {
    top: 38%;
  }
}

// Leadership certificate specific styles
.leadership-cert {
  .cert-content {
    top: 33%;
  }
  .cert-description {
    margin-bottom: 6px;
  }
  .cert-name {
    margin-bottom: 10px;
  }
  .cert-position {
    margin-bottom: 6px;
  }
  .cert-club-name {
    margin-bottom: 6px;
  }
  .cert-school-name {
    margin-bottom: 16px;
  }
  .cert-tenure {
    margin-bottom: 12px;
  }
}

// Advisory certificate specific styles
.advisory-cert {
  .cert-content {
    top: 30%;
  }
}
</style>
